import { useState, useCallback, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Select from "react-select";
import clsx from "clsx";

import { useQuery } from "@apollo/client";
import { QUERY_BROTHER_PRINTER_TEMPLATE_OPTIONS } from "../../../../../config/graphql/query";
import { useClientId } from "../../../../Clients/Client/hooks/useClient";
import useSocket from "../../../../../lib/hooks/useSocket";
import { reactSelectCustomStyles } from "../../../../Employees/Employee/Information";

const useBrotherPrinterTemplateOptionsQuery = (client = "") =>
  useQuery<{
    brotherPrinterTemplateOptions: IBadgeOptions;
  }>(QUERY_BROTHER_PRINTER_TEMPLATE_OPTIONS, {
    skip: !client,
    variables: { client },
  });

const BrotherPrinterSetting = ({
  disabled,
  screenId,
}: {
  disabled: boolean;
  screenId: string;
}) => {
  const clientId = useClientId();
  const { t } = useTranslation(["screens"]);
  const { watch, control } = useFormContext();

  const { data, loading } = useBrotherPrinterTemplateOptionsQuery(clientId);
  const { enabled } = data?.brotherPrinterTemplateOptions || {};
  const printingEnabled = watch("printing.enabled");

  const [printerOptions, setPrinterOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [loadingPrinters, setLoadingPrinters] = useState(false);

  const socket = useSocket<any>("/network/status", {
    message: (message) => {
      if (
        message.action === "printers-result" &&
        message.data.screen === screenId
      ) {
        setLoadingPrinters(false);

        if (message.data.status === "offline") {
          toast.error<string>(t("screens:screen.settings.printing.offline"));
          setPrinterOptions([]);
          return;
        }

        const options = (message.data.printers ?? []).map((p: string) => ({
          label: p,
          value: p,
        }));
        setPrinterOptions(options);
      }
    },
  });

  const requestPrinters = useCallback(() => {
    if (!socket) {
      return;
    }

    setLoadingPrinters(true);
    setPrinterOptions([]);

    socket.send(
      JSON.stringify({
        action: "request-printers",
        screen: screenId,
      }),
    );
  }, [socket, screenId]);

  useEffect(() => {
    if (enabled && printingEnabled) {
      requestPrinters();
    }
  }, [enabled, printingEnabled, requestPrinters]);

  if (!enabled) {
    return null;
  }

  return (
    <div className="form-group mb-4">
      <h6>Brother Printer</h6>
      <Controller
        name="printing.enabled"
        control={control}
        render={({ field }) => (
          <div className="form-check my-2">
            <input
              disabled={loading || disabled}
              id={field.name}
              type="checkbox"
              className="form-check-input"
              checked={field.value === true}
              onChange={() => field.onChange(!field.value)}
            />
            <label className="form-check-label" htmlFor={field.name}>
              {t("screens:screen.settings.printing.enabled")}
            </label>
          </div>
        )}
      />

      {printingEnabled && (
        <>
          <Controller
            name="printing.printer"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <div className="mt-3 ml-3">
                <label>
                  {t("screens:screen.settings.printing.selectPrinter")}
                </label>
                <Select
                  {...field}
                  options={printerOptions}
                  value={
                    printerOptions.find((o) => o.value === field.value) || null
                  }
                  onChange={(option) => field.onChange(option?.value ?? "")}
                  isLoading={loadingPrinters}
                  isDisabled={disabled || loadingPrinters}
                  styles={reactSelectCustomStyles(!!error)}
                  className={clsx({ "is-invalid": !!error })}
                  isClearable
                />
                {error && (
                  <div className="invalid-feedback">{error.message}</div>
                )}
              </div>
            )}
          />
          <Controller
            name="printing.isAirPrinting"
            control={control}
            render={({ field }) => (
              <div className="form-check mt-2 ml-3">
                <input
                  disabled={loading || disabled}
                  id={field.name}
                  type="checkbox"
                  className="form-check-input"
                  checked={field.value === true}
                  onChange={() => field.onChange(!field.value)}
                />
                <label className="form-check-label" htmlFor={field.name}>
                  {t("screens:screen.settings.printing.airprint")}
                </label>
              </div>
            )}
          />
          <Controller
            name="printing.isHorizontal"
            control={control}
            render={({ field }) => (
              <div className="form-check mt-2 ml-3">
                <input
                  disabled={loading || disabled}
                  id={field.name}
                  type="checkbox"
                  className="form-check-input"
                  checked={field.value === true}
                  onChange={() => field.onChange(!field.value)}
                />
                <label className="form-check-label" htmlFor={field.name}>
                  {t("screens:screen.settings.printing.landscape")}
                </label>
              </div>
            )}
          />
          <div className="form-row mt-2 ml-3">
            <Controller
              name="printing.label.width"
              control={control}
              render={({ field }) => (
                <div className="form-group col-auto">
                  <label htmlFor={field.name}>
                    {t("screens:screen.settings.printing.labelWidth")}
                  </label>
                  <input
                    id={field.name}
                    type="number"
                    className="form-control form-control-sm"
                    disabled={loading || disabled}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  />
                </div>
              )}
            />
            <Controller
              name="printing.label.height"
              control={control}
              render={({ field }) => (
                <div className="form-group col-auto">
                  <label htmlFor={field.name}>
                    {t("screens:screen.settings.printing.labelHeight")}
                  </label>
                  <input
                    id={field.name}
                    type="number"
                    className="form-control form-control-sm"
                    disabled={loading || disabled}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                  />
                </div>
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default BrotherPrinterSetting;
